import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { Eye, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import ExamesSolicitacao from "../components/examesSolicitacao";
import { StatusSelector } from "../components/StatusSelector";
import VisualizadorArquivo from "../components/visualizadorArquivo";
import { StatusAtendimento } from "../constants/statusAtendimento";
import { AvaliacaoCardiologicaProps } from "../interfaces/avaliacaoCardiologica.interface";
import { AvaliacaoCardiologicaFormData, getInitialData } from "../models/avaliacaoCardiologica.model";
import { buscarPdfParaVisualizacao } from "../utils/fetchPdfParaVisualizacao";
import { saveFormProtocolo } from "../utils/formSaveHelper";
import { CampoObrigatorio, validarCamposObrigatorios } from "../utils/formValidation";

export const AvaliacaoCardiologica = ({ onDataChange, initialData, atendimentoId, pacienteId, empresaId }: AvaliacaoCardiologicaProps) => {
  const [formData, setFormData] = useState<AvaliacaoCardiologicaFormData>(() => {
    const initialFormData = getInitialData(initialData);
    // Se não houver status_atendimento nos dados iniciais, definir como 'Novo'
    if (!initialFormData.status_atendimento) {
      return { ...initialFormData, status_atendimento: StatusAtendimento.NOVO };
    }
    return initialFormData;
  });
  const [statusAtendimento, setStatusAtendimento] = useState<string | null>(() => {
    const initialFormData = getInitialData(initialData);
    return initialFormData.status_atendimento || StatusAtendimento.NOVO;
  });
  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [protocoloId, setProtocoloId] = useState<number | undefined>(initialData?.id);

  // Sincronizar protocoloId quando initialData mudar
  useEffect(() => {
    if (initialData?.id) {
      setProtocoloId(initialData.id);
    }
  }, [initialData?.id]);

  // Mutation para salvar o formulário
  const saveMutation = useMutation({
    mutationFn: async (data: AvaliacaoCardiologicaFormData) => {
      return await saveFormProtocolo({
        endpoint: '/api/protocolo-avaliacao-cardiologica',
        data,
        atendimentoId: atendimentoId!,
        pacienteId: pacienteId || initialData?.id_paciente,
        empresaId: empresaId || initialData?.id_empresa,
        protocoloId: protocoloId || initialData?.id,
        statusAtendimento,
        formName: 'Avaliação Cardiológica (Escore Lee)'
      });
    },
    onSuccess: (response) => {
      // Extrair ID do response (pode estar em response.result.id ou response.data.result.id)
      const savedId = response?.result?.id || response?.data?.result?.id || protocoloId || initialData?.id;
      if (savedId && savedId !== protocoloId) {
        setProtocoloId(savedId);
        // Atualizar initialData através do callback
        onDataChange?.({ ...formData, id: savedId } as any);
      }
      
      // Disparar evento para atualizar painel de exames e documentos
      if (atendimentoId) {
        const event = new CustomEvent('examesAtualizados', {
          detail: { atendimentoId }
        });
        window.dispatchEvent(event);
      }
      
      toast.success("Formulário salvo com sucesso!");
    },
    onError: (error: any) => {
      console.error('Erro ao salvar avaliação cardiológica:', error);
      toast.error("Não foi possível salvar o formulário.");
    }
  });

  const handleInputChange = useCallback((field: string, value: any) => {
    setFormData((prevData) => {
      const newData = { 
        ...prevData, 
        [field]: value,
        // Sempre manter o status_atendimento no formData se ele estiver definido
        ...(statusAtendimento && { status_atendimento: statusAtendimento })
      };
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleSwitchChange = useCallback((field: string, checked: boolean) => {
    setFormData((prevData) => {
      const newData = { 
        ...prevData, 
        [field]: checked,
        // Sempre manter o status_atendimento no formData se ele estiver definido
        ...(statusAtendimento && { status_atendimento: statusAtendimento })
      };
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleSave = useCallback(async () => {
    if (!atendimentoId) {
      toast.error("ID do atendimento não fornecido.");
      return;
    }

    // Definir campos obrigatórios para Avaliação Cardiológica
    // Nota: Segundo o schema do backend, todos os campos são opcionais
    const camposObrigatorios: CampoObrigatorio[] = [];

    const dataToSave = statusAtendimento 
      ? { ...formData, status_atendimento: statusAtendimento } as any
      : formData;

    // Validar campos obrigatórios
    if (!validarCamposObrigatorios(dataToSave, camposObrigatorios)) {
      return;
    }

    try {
      console.log('Salvando avaliação cardiológica:', { endpoint: '/api/protocolo-avaliacao-cardiologica', data: dataToSave, atendimentoId });
      await saveMutation.mutateAsync(dataToSave);
      console.log('Avaliação cardiológica salva com sucesso');
    } catch (error) {
      console.error('Erro ao salvar avaliação cardiológica:', error);
      toast.error("Não foi possível salvar o formulário. Verifique o console para mais detalhes.");
    }
  }, [atendimentoId, formData, statusAtendimento, saveMutation]);

  // Função para visualizar o PDF
  const handleVisualizar = useCallback(async () => {
    const currentProtocoloId = protocoloId || initialData?.id;
    
    if (!currentProtocoloId) {
      toast.warning("Salve o formulário antes de visualizar.");
      return;
    }

    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const url = `${BASE_URL}/api/protocolo-avaliacao-cardiologica/${currentProtocoloId}/imprimir`;
      const blob = await buscarPdfParaVisualizacao({
        atendimentoId: atendimentoId,
        protocoloId: currentProtocoloId,
        tipoProtocolo: "avaliacao_cardiologica",
        fallbackUrl: url
      });

      const urlBlob = window.URL.createObjectURL(blob);
      setPdfUrl(urlBlob);
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(error?.message || "Não foi possível gerar o PDF.");
    }
  }, [protocoloId, initialData?.id, atendimentoId]);

  const fecharVisualizador = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setVisualizadorAberto(false);
    setPdfUrl(null);
  };

  // Calcular escore Lee baseado nos fatores selecionados
  const calculateLeeScore = () => {
    const factors = [
      formData.historiaInfartoMiocardio,
      formData.insuficienciaCardiacaSintomatica,
      formData.doencaCerebrovascular,
      formData.diabetesMellitusInsulina,
      formData.insuficienciaRenalCronica,
      formData.cirurgiaGrandePorte
    ];
    
    const score = factors.filter(Boolean).length;
    if (score === 0) return { risk: "Baixo Risco", complications: "< 1%" };
    if (score >= 1 && score <= 2) return { risk: "Risco Moderado", complications: "1-5%" };
    if (score >= 3) return { risk: "Alto Risco", complications: "> 5%" };
    return { risk: "Baixo Risco", complications: "< 1%" };
  };

  const leeScore = calculateLeeScore();

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending 
            ? (protocoloId || initialData?.id ? 'Atualizando...' : 'Salvando...') 
            : (protocoloId || initialData?.id ? 'Atualizar' : 'Salvar')}
        </Button>
        <Button 
          variant="outline" 
          onClick={handleVisualizar}
          disabled={!protocoloId && !initialData?.id}
          title={!protocoloId && !initialData?.id ? "Salve o formulário antes de visualizar" : "Visualizar PDF"}
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizar Documento
        </Button>
      </div>
      <StatusSelector 
        atendimentoId={atendimentoId} 
        formName="Avaliação Cardiológica (Escore Lee)"
        value={statusAtendimento || formData.status_atendimento}
        onStatusSelect={(status) => {
          setStatusAtendimento(status || null);
          // Atualizar também no formData
          setFormData((prevData) => ({
            ...prevData,
            status_atendimento: status || undefined
          }));
          handleInputChange('status_atendimento', status || '');
        }}
      />
      {/* Card Histórico Clínico */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico Clínico</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="hipertensaoArterial">Hipertensão arterial</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="hipertensaoArterial"
                checked={formData.hipertensaoArterial}
                onCheckedChange={(checked) => handleSwitchChange('hipertensaoArterial', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="diabetesMellitus">Diabetes Mellitus</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="diabetesMellitus"
                checked={formData.diabetesMellitus}
                onCheckedChange={(checked) => handleSwitchChange('diabetesMellitus', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="doencaCardiacaPrevia">Doença cardíaca prévia (ex: infarto, ICC)</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="doencaCardiacaPrevia"
                checked={formData.doencaCardiacaPrevia}
                onCheckedChange={(checked) => handleSwitchChange('doencaCardiacaPrevia', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="historiaInsuficienciaRenal">História de insuficiência renal</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="historiaInsuficienciaRenal"
                checked={formData.historiaInsuficienciaRenal}
                onCheckedChange={(checked) => handleSwitchChange('historiaInsuficienciaRenal', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div>
            <Label htmlFor="procedimentosCirurgicosPrevios">Procedimentos cirúrgicos prévios</Label>
            <Textarea
              id="procedimentosCirurgicosPrevios"
              placeholder="Descrever procedimentos cirúrgicos prévios..."
              rows={3}
              value={formData.procedimentosCirurgicosPrevios}
              onChange={(e) => handleInputChange('procedimentosCirurgicosPrevios', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Card Avaliação pelo Índice de Lee */}
      <Card>
        <CardHeader>
          <CardTitle>Avaliação pelo Índice de Lee (fatores de risco cardíaco)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="historiaInfartoMiocardio">História de infarto do miocárdio</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="historiaInfartoMiocardio"
                checked={formData.historiaInfartoMiocardio}
                onCheckedChange={(checked) => handleSwitchChange('historiaInfartoMiocardio', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="insuficienciaCardiacaSintomatica">Insuficiência cardíaca sintomática</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="insuficienciaCardiacaSintomatica"
                checked={formData.insuficienciaCardiacaSintomatica}
                onCheckedChange={(checked) => handleSwitchChange('insuficienciaCardiacaSintomatica', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="doencaCerebrovascular">Doença cerebrovascular (AVC ou AIT)</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="doencaCerebrovascular"
                checked={formData.doencaCerebrovascular}
                onCheckedChange={(checked) => handleSwitchChange('doencaCerebrovascular', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="diabetesMellitusInsulina">Diabetes mellitus em uso de insulina</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="diabetesMellitusInsulina"
                checked={formData.diabetesMellitusInsulina}
                onCheckedChange={(checked) => handleSwitchChange('diabetesMellitusInsulina', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="insuficienciaRenalCronica">Insuficiência renal crônica</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="insuficienciaRenalCronica"
                checked={formData.insuficienciaRenalCronica}
                onCheckedChange={(checked) => handleSwitchChange('insuficienciaRenalCronica', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="cirurgiaGrandePorte">Cirurgia de grande porte (ex: intra-abdominal, torácica)</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="cirurgiaGrandePorte"
                checked={formData.cirurgiaGrandePorte}
                onCheckedChange={(checked) => handleSwitchChange('cirurgiaGrandePorte', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="mt-4 p-4 bg-muted rounded-lg">
            <Label className="text-base font-semibold mb-2 block">Fatores de Risco (Escore Lee)</Label>
            <p className="text-sm font-semibold">
              Resultado: {leeScore.risk}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Complicações: {leeScore.complications}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Card Exame Físico */}
      <Card>
        <CardHeader>
          <CardTitle>Exame Físico</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="pressaoArterial">PA</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="pressaoArterial"
                  placeholder="Ex: 120/80"
                  value={formData.pressaoArterial}
                  onChange={(e) => handleInputChange('pressaoArterial', e.target.value)}
                />
                <span className="text-sm text-muted-foreground">mmHg</span>
              </div>
            </div>
            <div>
              <Label htmlFor="frequenciaCardiaca">FC</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="frequenciaCardiaca"
                  placeholder="Ex: 72"
                  value={formData.frequenciaCardiaca}
                  onChange={(e) => handleInputChange('frequenciaCardiaca', e.target.value)}
                />
                <span className="text-sm text-muted-foreground">bpm</span>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="auscultaCardiacaPulmonar">Ausculta cardíaca e pulmonar</Label>
            <Input
              id="auscultaCardiacaPulmonar"
              placeholder="Descrever ausculta cardíaca e pulmonar..."
              value={formData.auscultaCardiacaPulmonar}
              onChange={(e) => handleInputChange('auscultaCardiacaPulmonar', e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="edemaSinaisIC">Edema, sinais de IC</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch
                id="edemaSinaisIC"
                checked={formData.edemaSinaisIC}
                onCheckedChange={(checked) => handleSwitchChange('edemaSinaisIC', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div>
            <Label className="text-base font-semibold mb-2 block">Exames complementares</Label>
            <div className="space-y-4">
              <div>
                <Label htmlFor="eletrocardiograma">Eletrocardiograma</Label>
                <Textarea
                  id="eletrocardiograma"
                  placeholder="Resultado do eletrocardiograma..."
                  rows={2}
                  value={formData.eletrocardiograma}
                  onChange={(e) => handleInputChange('eletrocardiograma', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="ecocardiograma">Ecocardiograma (se realizado)</Label>
                <Textarea
                  id="ecocardiograma"
                  placeholder="Resultado do ecocardiograma..."
                  rows={2}
                  value={formData.ecocardiograma}
                  onChange={(e) => handleInputChange('ecocardiograma', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="outrosExames">Outros</Label>
                <Textarea
                  id="outrosExames"
                  placeholder="Outros exames complementares..."
                  rows={2}
                  value={formData.outrosExames}
                  onChange={(e) => handleInputChange('outrosExames', e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Diagnóstico e Conduta */}
      <Card>
        <CardHeader>
          <CardTitle>Diagnóstico e Conduta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="riscoCirurgicoCardiaco">Risco cirúrgico cardíaco</Label>
            <RadioGroup
              id="riscoCirurgicoCardiaco"
              value={formData.riscoCirurgicoCardiaco}
              onValueChange={(value) => handleInputChange('riscoCirurgicoCardiaco', value)}
              className="mt-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="baixo" id="risco-baixo" />
                <Label htmlFor="risco-baixo" className="font-normal cursor-pointer">Baixo</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="moderado" id="risco-moderado" />
                <Label htmlFor="risco-moderado" className="font-normal cursor-pointer">Moderado</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="alto" id="risco-alto" />
                <Label htmlFor="risco-alto" className="font-normal cursor-pointer">Alto</Label>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="recomendacoesPreOperatorias">Recomendações pré-operatórias</Label>
            <Textarea
              id="recomendacoesPreOperatorias"
              placeholder="Recomendações pré-operatórias..."
              rows={4}
              value={formData.recomendacoesPreOperatorias}
              onChange={(e) => handleInputChange('recomendacoesPreOperatorias', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Componente de Exames Solicitados */}
      <ExamesSolicitacao
        initialExams={Array.isArray(initialData?.examesSolicitados) ? initialData.examesSolicitados : []}
        onExamsChange={(exams) => {
          const newData = { ...formData, examesSolicitados: exams };
          setFormData(newData);
          onDataChange?.(newData);
        }}
        protocoloId={protocoloId || initialData?.id}
      />
      {/* Visualizador de PDF */}
      {pdfUrl && (
        <VisualizadorArquivo
          arquivoUrl={pdfUrl}
          nomeArquivo={`avaliacao_cardiologica_${protocoloId || initialData?.id || 'novo'}.pdf`}
          tipoArquivo="pdf"
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </div>
  );
};

export default AvaliacaoCardiologica;
